import { useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

const useSessionManager = () => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const checkIntervalRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  const INACTIVITY_TIMEOUT = 12 * 60 * 60 * 1000; // 12 hours inactivity timeout 
  
  const checkTokenExpiry = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      setIsSessionExpired(true);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp < currentTime) {
        setIsSessionExpired(true);
        clearInterval(checkIntervalRef.current);
        // Clean up session-related localStorage items
        localStorage.removeItem('justLoggedIn');
      }
    } catch (error) {
      console.error('Token decode error:', error);
      setIsSessionExpired(true);
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      setIsSessionExpired(true);
    }, INACTIVITY_TIMEOUT);
  }, [INACTIVITY_TIMEOUT]);

  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    checkTokenExpiry();
    checkIntervalRef.current = setInterval(checkTokenExpiry, 60000);

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity);
    });

    resetInactivityTimer();

    // Listen for session expired events from axios interceptor
    const handleSessionExpired = () => {
      setIsSessionExpired(true);
    };
    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [checkTokenExpiry, handleUserActivity, resetInactivityTimer]);

  const closeModal = useCallback(() => {
    setIsSessionExpired(false);
  }, []);

  return {
    isSessionExpired,
    closeModal,
  };
};

export default useSessionManager;